import React, { useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, MapPinIcon, ArrowUpRightIcon, MailIcon, StarIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
import { PILLARS, PARTICIPATION, JOIN_STEPS, PRESIDENT_LETTER } from '../data/national-network-content';
const NetworkMap = lazy(() => import('../components/NetworkMap').then(m => ({
  default: m.NetworkMap
})));
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/eZ5rmta1LUT5zNjEpkMWnd/HAA_National_Network.png';
const CONTACT_EMAIL = 'eva@honduranamerican.org';
const NAVY = '#0b1f3f';
const NAVY_DEEP = '#071427';
const BLUE = '#1b5fbe';
const SKY = '#3FA9F5';
const RED = '#e0343c';
const PALE = '#eef3fa';
const EASE = [0.16, 1, 0.3, 1] as const;
function SectionLabel({
  children,
  onDark = false
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return <p className="inline-flex items-center gap-2.5 font-nunito text-[10px] font-bold uppercase tracking-[0.3em] mb-5" style={{
    color: onDark ? SKY : BLUE
  }} data-id="element-3786" data-mp="2lwu1abzhgpcy">
      <StarIcon className="w-3 h-3" style={{
      color: RED
    }} fill={RED} data-id="element-3787" data-mp="2c7k78ql2wwm7" />
      {children}
    </p>;
}
export function NationalNetworkPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const mailto = (subject: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  const joinHref = mailto(isEs ? 'Solicitud de Membresía — Red Nacional Hondureño-Americana' : 'Membership Application — Honduran-American National Network');
  return <div className="min-h-screen w-full font-nunito antialiased" style={{
    backgroundColor: '#ffffff',
    color: NAVY
  }} data-id="element-3788" data-mp="3nn8tffqidrly">
      {/* Header — white bar carrying the National Network lockup */}
      <header className="sticky top-0 z-30 w-full bg-white shadow-sm" data-id="element-3789" data-mp="1gz8k40l27p7x">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-4" data-id="element-3790" data-mp="1gwvtkz18k6j9">
          <a href="#" className="flex items-center group" aria-label="Honduran American Association National Network" data-id="element-3791" data-mp="1lrrt0k8sxw6e">
            <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-11 sm:h-14 w-auto object-contain group-hover:opacity-80 transition-opacity" data-id="element-3792" data-mp="1c0owzwuqc7dz" />
          </a>
          <div className="flex items-center gap-4" data-id="element-3793" data-mp="1qos1t2x1dd17">
            <div className="inline-flex rounded-full border overflow-hidden" style={{
            borderColor: 'rgba(11,31,63,0.25)'
          }} role="group" aria-label={isEs ? 'Idioma' : 'Language'} data-id="element-3794" data-mp="1qo08xzutptem">
              {(['EN', 'ES'] as const).map(lang => <button key={lang} type="button" onClick={() => setLanguage(lang)} aria-pressed={language === lang} className="px-3.5 py-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
              backgroundColor: language === lang ? NAVY : 'transparent',
              color: language === lang ? '#ffffff' : 'rgba(11,31,63,0.6)'
            }} data-id="element-3795" data-mp="kaammalxdd72">
                  {lang}
                </button>)}
            </div>
            <a href={joinHref} className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
            backgroundColor: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = NAVY;
          }} data-id="element-3796" data-mp="3k5fpuwk238rf">
              {isEs ? 'Únase' : 'Join'}
            </a>
            <a href="#" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] transition-opacity hover:opacity-60" style={{
            color: NAVY
          }} data-id="element-3797" data-mp="29pifox558tnv">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-3798" data-mp="2ij55m6mo72p5" />
              {isEs ? 'Volver' : 'Back'}
            </a>
          </div>
        </div>
        <div className="h-[3px] w-full flex" data-id="element-3799" data-mp="2seozknhobrb9">
          <div className="flex-1" style={{
          backgroundColor: BLUE
        }} data-id="element-3800" data-mp="2sddp64tq3lb6" />
          <div className="flex-1 bg-white" data-id="element-3801" data-mp="2xfcsxftdkk8n" />
          <div className="flex-1" style={{
          backgroundColor: RED
        }} data-id="element-3802" data-mp="2xcgtto5sjpim" />
        </div>
      </header>

      <main data-id="element-3803" data-mp="31dk9a9jxfjjb">
        {/* 1 — Hero */}
        <section className="relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-3804" data-mp="1312251t7zg08">
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.13]" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.25) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)'
        }} data-id="element-3805" data-mp="172nb4c2mva55" />
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-16 lg:pt-20 lg:pb-20 text-center" data-id="element-3806" data-mp="1dinhjhef83jj">
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} data-id="element-3807" data-mp="19epif6p3isqh">
              <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-[0.03em] leading-[1.02] mb-8" data-id="element-3808" data-mp="3u8ch67k7ba44">
                <span className="block" style={{
                color: SKY
              }} data-id="element-3809" data-mp="3p6ddewkjub6n">
                  {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
                </span>
                <span className="block text-white" data-id="element-3810" data-mp="3vlu9p7cxhkhk">
                  {isEs ? 'Mayor Conexión' : 'Greater Connection'}
                </span>
                <span className="block" style={{
                color: RED
              }} data-id="element-3811" data-mp="3lozf2zjs048k">
                  {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
                </span>
              </h1>
              <p className="font-nunito text-white/70 text-lg leading-relaxed max-w-2xl mx-auto mb-9" data-id="element-3812" data-mp="3cost0pa0hvxs">
                {isEs ? 'Un espacio nacional que conecta a las organizaciones, líderes y socios que ya sirven a las comunidades hondureñas en los Estados Unidos y en Honduras.' : 'A national space connecting the organizations, leaders, and partners already serving Honduran communities in the United States and in Honduras.'}
              </p>
              <a href={joinHref} className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
              color: NAVY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = RED;
              e.currentTarget.style.color = '#ffffff';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = NAVY;
            }} data-id="element-3813" data-mp="2u0e1m0vs8fwg">
                {isEs ? 'Únase a la Red' : 'Join the Network'}
                <ArrowUpRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-3814" data-mp="333jeyglunrab" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Logo band */}
        <section className="bg-white" data-id="element-3815" data-mp="373tno1110vsv">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 lg:py-7 flex justify-center" data-id="element-3816" data-mp="374v8gx7ugwns">
            <motion.img initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            ease: EASE
          }} src="https://cdn.magicpatterns.com/uploads/xp6mxLQyMd62ZaHp36n7MC/HAA_National_Network.png" alt="Honduran American Association National Network" className="w-[clamp(18rem,58vw,44rem)] h-auto object-contain" data-id="element-3817" data-mp="i9lmrf7zb0ul" />
          </div>
        </section>

        {/* 2 — Letter from the President */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3818" data-mp="8j909hglsxlt">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3819" data-mp="8ih7eeee5dz8">
            <SectionLabel data-id="element-3820" data-mp="dcbn76qvl8t1">
              {isEs ? 'Carta del Presidente' : 'Letter from the President'}
            </SectionLabel>
            <p className="font-bebas text-3xl tracking-[0.04em] mb-7" style={{
            color: BLUE
          }} data-id="element-3821" data-mp="3h03te57ln3mp">
              {isEs ? PRESIDENT_LETTER.salutationEs : PRESIDENT_LETTER.salutation}
            </p>
            <div className="space-y-5 font-nunito text-base sm:text-[1.0625rem] leading-relaxed" style={{
            color: 'rgba(11,31,63,0.78)'
          }} data-id="element-3822" data-mp="34iavc3t47f1l">
              {(isEs ? PRESIDENT_LETTER.paragraphsEs : PRESIDENT_LETTER.paragraphs).map(p => <p key={p.slice(0, 40)} data-id="element-3823" data-mp="2lr07xwmllkeg">{p}</p>)}
            </div>
            <div className="mt-10 pt-7 border-t" style={{
            borderColor: 'rgba(11,31,63,0.15)'
          }} data-id="element-3824" data-mp="2lox86kvn8cwj">
              <p className="font-nunito text-sm mb-7" style={{
              color: 'rgba(11,31,63,0.6)'
            }} data-id="element-3825" data-mp="2btcct5l2yavr">
                {isEs ? PRESIDENT_LETTER.closingEs : PRESIDENT_LETTER.closing}
              </p>
              <div className="grid sm:grid-cols-2 gap-8 sm:gap-6" data-id="element-3826" data-mp="22snzkd3igejx">
                {[{
                src: 'https://cdn.magicpatterns.com/uploads/mcPsQzGFDU6t8Dm21iMLQ5/154111606_32s61fjkqp-GREY.jpg',
                name: PRESIDENT_LETTER.name,
                role: isEs ? PRESIDENT_LETTER.roleEs : PRESIDENT_LETTER.role,
                position: '50% 22%',
                zoom: 1.28
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/kb8rqEBdvwMudH3CyTb87Y/ECallejas.jpg',
                name: 'Eva Callejas',
                role: isEs ? 'Vicepresidenta de Alianzas e Iniciativas Estratégicas' : 'Vice President of Partnerships & Strategic Initiatives',
                position: '50% 18%',
                zoom: 1
              }].map(person => <div key={person.name} className="flex items-center gap-5 sm:gap-6" data-id="element-3827" data-mp="1f3rjke0e1tcy">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden flex-shrink-0" style={{
                  border: `3px solid ${BLUE}`
                }} data-id="element-3828" data-mp="1mqokj0us44e3">
                      <img src={person.src} alt={`${person.name}, ${person.role}`} className="w-full h-full object-cover" style={{
                    objectPosition: person.position,
                    transform: `scale(${person.zoom})`
                  }} data-id="element-3829" data-mp="1hp8x5254plsj" />
                    </div>
                    <div data-id="element-3830" data-mp="20nbk7be498np">
                      <p className="font-bebas text-2xl tracking-[0.04em] leading-tight" style={{
                    color: NAVY
                  }} data-id="element-3831" data-mp="20mjycsabmpam">
                        {person.name}
                      </p>
                      <p className="font-nunito text-sm leading-snug" style={{
                    color: 'rgba(11,31,63,0.6)'
                  }} data-id="element-3832" data-mp="3304k7bq9f5gv">
                        {person.role}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* 3 — Pillars of Collaboration */}
        <section style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3833" data-mp="2k1scluezy3ne">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3834" data-mp="2k3d75k1b9ml9">
            <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-3835" data-mp="2f8pcf3fprvy8">
              <SectionLabel onDark data-id="element-3836" data-mp="2ba03bn1pl0ke">
                {isEs ? '01 — La Red' : '01 — The Network'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: '#ffffff'
            }} data-id="element-3837" data-mp="26byk924a918x">
                {isEs ? 'Pilares de Colaboración' : 'Pillars of Collaboration'}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12" data-id="element-3838" data-mp="1m7hgsalwihv4">
              {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return <motion.article key={pillar.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="relative pl-14" data-id="element-3839" data-mp="1w6q8ewtankeb" data-mpi={i}>
                    <span className="absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center font-bebas text-base tracking-wide text-white" style={{
                  backgroundColor: i % 2 === 0 ? SKY : 'rgba(255,255,255,0.14)'
                }} aria-hidden="true" data-id="element-3840" data-mp="1i77b6z20ttgs">
                      {i + 1}
                    </span>
                    <Icon className="w-6 h-6 mb-4" style={{
                  color: SKY
                }} strokeWidth={1.6} data-id="element-3841" data-mp="315deoglkj7k2" />
                    <h3 className="font-bebas text-2xl tracking-[0.04em] leading-tight mb-3 text-white" data-id="element-3842" data-mp="3a5iarulbajix">
                      {isEs ? pillar.titleEs : pillar.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(255,255,255,0.68)'
                }} data-id="element-3843" data-mp="3k2wrlvb5dabr">
                      {isEs ? pillar.textEs : pillar.text}
                    </p>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* 4 — Year One participation */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3844" data-mp="9l4b952tyjx">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3845" data-mp="58fj7zzxb2yr">
            <div className="max-w-2xl mb-14" data-id="element-3846" data-mp="3moe0w78uuglj">
              <SectionLabel data-id="element-3847" data-mp="3mnmdhjpevp6c">
                {isEs ? '02 — Participación' : '02 — Participation'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" style={{
              color: NAVY
            }} data-id="element-3848" data-mp="3rle1w3qukt5s">
                {isEs ? 'Cómo Se Ve la Participación en el Primer Año' : 'What Participation Can Look Like in Year One'}
              </h2>
              <p className="font-nunito text-base leading-relaxed" style={{
              color: 'rgba(11,31,63,0.7)'
            }} data-id="element-3849" data-mp="38ntfd3bk26oc">
                {isEs ? 'Construyendo conexiones, oportunidades e impacto compartido. Las organizaciones participantes podrán involucrarse de maneras que se alineen con su misión, capacidad y prioridades.' : 'Building connections, opportunities, and shared impact together. Participating organizations will have opportunities to engage in ways that align with their mission, capacity, and priorities.'}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3850" data-mp="rhw57sbr9qgl">
              {PARTICIPATION.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="rounded-xl border bg-white p-7 h-full flex flex-col" style={{
                borderColor: 'rgba(11,31,63,0.12)'
              }} data-id="element-3851" data-mp="1j8seyk2irezn" data-mpi={i}>
                    <Icon className="w-5 h-5 mb-4" style={{
                  color: BLUE
                }} strokeWidth={1.7} data-id="element-3852" data-mp="3pt7pn98e22v4" />
                    <h3 className="font-bebas text-xl tracking-[0.06em] uppercase mb-3" style={{
                  color: NAVY
                }} data-id="element-3853" data-mp="2o023sxyem1u">
                      {isEs ? item.titleEs : item.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(11,31,63,0.68)'
                }} data-id="element-3854" data-mp="cjjx8d4p1gpj">
                      {isEs ? item.textEs : item.text}
                    </p>
                  </motion.div>;
            })}
            </div>

            <p className="font-nunito text-sm sm:text-base leading-relaxed max-w-3xl mt-10" style={{
            color: 'rgba(11,31,63,0.68)'
          }} data-id="element-3855" data-mp="vbmftu0wb72f">
              {isEs ? 'La Red está diseñada para ser flexible. Las organizaciones pueden participar en las oportunidades más relevantes para su trabajo y capacidad. Nuestra meta es facilitar que las organizaciones se conecten, colaboren, compartan recursos y persigan oportunidades juntas.' : 'The Network is designed to be flexible. Organizations may participate in the opportunities most relevant to their work and capacity. Our goal is to make it easier for organizations to connect, collaborate, share resources, and pursue opportunities together.'}
            </p>
          </div>
        </section>

        {/* 5 — How do I join */}
        <section className="bg-white" data-id="element-3856" data-mp="3no71yvh2ijf2">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3857" data-mp="2zzntaeu3w4mg">
            <div className="text-center mb-14" data-id="element-3858" data-mp="2zxtap76b4olj">
              <SectionLabel data-id="element-3859" data-mp="2vz3ul6tvwsy7">
                {isEs ? '03 — Membresía' : '03 — Membership'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: NAVY
            }} data-id="element-3860" data-mp="2ltlxfr8spt48">
                {isEs ? '¿Cómo Me Uno?' : 'How Do I Join?'}
              </h2>
            </div>

            <ol className="space-y-10" data-id="element-3861" data-mp="2gurfssmkealp">
              {JOIN_STEPS.map((step, i) => <motion.li key={step.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="relative pl-16 sm:pl-20 pb-10 border-b last:border-b-0 last:pb-0" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3862" data-mp="34l4wxo3m2p1i" data-mpi={i}>
                  <span className="absolute left-0 top-0 w-11 h-11 rounded-full flex items-center justify-center font-bebas text-lg tracking-wide text-white" style={{
                backgroundColor: i === 1 ? NAVY : BLUE
              }} aria-hidden="true" data-id="element-3863" data-mp="1o4910uchaksg">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-bebas text-3xl tracking-[0.04em] mb-4" style={{
                color: BLUE
              }} data-id="element-3864" data-mp="jyh2alryeoxx">
                    {isEs ? step.titleEs : step.title}
                  </h3>
                  <p className="font-nunito text-base leading-relaxed" style={{
                color: 'rgba(11,31,63,0.78)'
              }} data-id="element-3865" data-mp="3sgdh07xlczus">
                    {isEs ? step.leadEs : step.lead}
                  </p>
                  {(isEs ? step.pointsEs : step.points).length > 0 ? <ul className="mt-5 space-y-3" data-id="element-3866" data-mp="3nhad4qtckzoe">
                      {(isEs ? step.pointsEs : step.points).map(point => <li key={point.slice(0, 40)} className="flex gap-3" data-id="element-3867" data-mp="33vh4tzg32qs7">
                          <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0" style={{
                    color: RED
                  }} strokeWidth={2.5} data-id="element-3868" data-mp="2yxfm5fmkkth9" />
                          <span className="font-nunito text-sm leading-relaxed" style={{
                    color: 'rgba(11,31,63,0.68)'
                  }} data-id="element-3869" data-mp="38wel26hj9ar2">
                            {point}
                          </span>
                        </li>)}
                    </ul> : null}
                  {i === 0 ? <a href={joinHref} className="inline-flex items-center gap-2 mt-5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                color: BLUE
              }} data-id="element-3870" data-mp="3rn5q4vuhmezn">
                      <MailIcon className="w-3.5 h-3.5" data-id="element-3871" data-mp="ceowrpjgsswb" />
                      {isEs ? 'Solicitar información' : 'Request information'}
                    </a> : null}
                </motion.li>)}
            </ol>
          </div>
        </section>

        {/* 6 — Map */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3872" data-mp="r7im02ojgne6">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3873" data-mp="v69bbnmrj0qq">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10" data-id="element-3874" data-mp="105ktn12s2eaf">
              <div className="max-w-xl" data-id="element-3875" data-mp="155qfg0md96kl">
                <SectionLabel onDark data-id="element-3876" data-mp="156rxwq5povcc">
                  {isEs ? '04 — Alcance' : '04 — Reach'}
                </SectionLabel>
                <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] text-white" data-id="element-3877" data-mp="1a2h256sof0ez">
                  {isEs ? 'Explore la Red' : 'Explore the Network'}
                </h2>
              </div>
              <p className="font-nunito text-white/50 text-xs uppercase tracking-[0.16em] max-w-xs" data-id="element-3878" data-mp="1t0sekjt7ozmt">
                {isEs ? 'Las organizaciones aparecerán aquí conforme se unan' : 'Organizations will appear here as they join'}
              </p>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/12" style={{
            backgroundColor: NAVY_DEEP
          }} data-id="element-3879" data-mp="1f9dvkczb0yfr">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10" data-id="element-3880" data-mp="2bz4pa9zruhki">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: RED
              }} data-id="element-3881" data-mp="27ykrr5badbxu" />
                <span className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.22em]" data-id="element-3882" data-mp="1xzvgvwht4rr3">
                  {isEs ? 'Mapa de la Red' : 'Network Map'}
                </span>
              </div>
              <div className="relative bg-white" data-id="element-3883" data-mp="mk6xff3riav1">
                <img src="https://cdn.magicpatterns.com/patterns/generated-images/3c2f66c0-481c-423e-892e-ac32122027f2.jpg" alt={isEs ? 'Mapa de los Estados Unidos' : 'Map of the United States'} className="w-full h-auto block" data-id="element-3884" data-mp="mjf0o0yfi736" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-3885" data-mp="26gm2x8kkatmh">
                  <div className="rounded-full px-7 py-3" style={{
                  backgroundColor: NAVY
                }} data-id="element-3886" data-mp="2u6gja043b0bh">
                    <span className="font-bebas text-white text-xl sm:text-2xl tracking-[0.14em] uppercase" data-id="element-3887" data-mp="2y4nvqiritfgv">
                      {isEs ? 'Próximamente' : 'Coming Soon'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7 — Roster (renders once organizations are added) */}
        {SISTER_ORGANIZATIONS.length > 0 ? <section className="bg-white" data-id="element-3888" data-mp="1674lwhf5bsx3">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3889" data-mp="1662x7a57j7ww">
            <div className="max-w-2xl mb-12" data-id="element-3890" data-mp="117i03jl954co">
              <SectionLabel data-id="element-3891" data-mp="11avy6fmvureb">
                {isEs ? '05 — Organizaciones Participantes' : '05 — Participating Organizations'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" style={{
              color: NAVY
            }} data-id="element-3892" data-mp="1a753gaxjkk8d">
                {isEs ? 'Quiénes Forman la Red' : 'Who Makes Up the Network'}
              </h2>
              <p className="font-nunito text-base leading-relaxed" style={{
              color: 'rgba(11,31,63,0.68)'
            }} data-id="element-3893" data-mp="wa88pyeayim7">
                {isEs ? 'Cada organización aporta una fortaleza distinta: educación, salud mental, artes, defensa legal, desarrollo juvenil. Esta es la base de la Red, y continúa creciendo.' : 'Each organization brings a distinct strength — education, mental health, the arts, legal advocacy, youth development. This is the foundation of the Network, and it continues to grow.'}
              </p>
            </div>

            <ul className="border-t" style={{
            borderColor: 'rgba(11,31,63,0.12)'
          }} data-id="element-3894" data-mp="28mna5gnybiqp">
              {SISTER_ORGANIZATIONS.map((org, i) => <motion.li key={org.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="border-b transition-colors duration-200 hover:bg-[#f5f8fc]" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3895" data-mp="1fyrg6jpro4xm" data-mpi={i}>
                  <div className="py-8 grid lg:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.15fr)_auto] gap-x-8 gap-y-4 items-start px-2 sm:px-4" data-id="element-3896" data-mp="tpkd5v0kaobz">
                    <span className="font-nunito text-[10px] font-bold tabular-nums tracking-[0.1em] pt-2" style={{
                  color: 'rgba(11,31,63,0.35)'
                }} data-id="element-3897" data-mp="pnz3sjn8ks6j">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-3898" data-mp="b04c96kystv1">
                      <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-2.5" style={{
                    color: BLUE
                  }} data-id="element-3899" data-mp="aw6ou8rctg5c">
                        {isEs ? org.focusEs : org.focus}
                      </p>
                      <h3 className="font-bebas text-2xl sm:text-[1.7rem] tracking-[0.03em] leading-tight mb-2" style={{
                    color: NAVY
                  }} data-id="element-3900" data-mp="3t60hvajaqz55">
                        {org.name}
                      </h3>
                      <p className="inline-flex items-center gap-1.5 font-nunito text-xs uppercase tracking-[0.12em]" style={{
                    color: 'rgba(11,31,63,0.55)'
                  }} data-id="element-3901" data-mp="108i3tqsftgyn">
                        <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                      color: RED
                    }} data-id="element-3902" data-mp="qfje25l7fkdg" />
                        {org.location}
                      </p>
                    </div>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(11,31,63,0.68)'
                }} data-id="element-3903" data-mp="ljlwxx24a9zm">
                      {isEs ? org.descriptionEs : org.description}
                    </p>
                    <div className="flex flex-col gap-2.5 lg:items-end lg:text-right" data-id="element-3904" data-mp="21x2b1g86yr3k">
                      {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity whitespace-nowrap hover:opacity-70" style={{
                    color: BLUE
                  }} data-id="element-3905" data-mp="26w5j79jgxdfr">
                          {isEs ? 'Sitio web' : 'Website'}
                          <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3906" data-mp="dyicdh855xjo" />
                        </a> : null}
                      <a href={mailto(`${isEs ? 'Contacto' : 'Contact'} — ${org.name}`)} className="inline-flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70 whitespace-nowrap" style={{
                    color: 'rgba(11,31,63,0.55)'
                  }} data-id="element-3907" data-mp="8z5f02jn982u">
                        <MailIcon className="w-3.5 h-3.5" data-id="element-3908" data-mp="2kru86gonx9vs" />
                        {isEs ? 'Contactar' : 'Contact'}
                      </a>
                    </div>
                  </div>
                </motion.li>)}
            </ul>
          </div>
        </section> : null}

        {/* 8 — CTA */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3909" data-mp="5iftcxcuelgt">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center" data-id="element-3910" data-mp="5hxlt4kqcktl">
            <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[1.02] mb-7" data-id="element-3911" data-mp="knzhxsn0p9z">
              <span className="block" style={{
              color: SKY
            }} data-id="element-3912" data-mp="3rnt4fuqlpnw3">
                {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
              </span>
              <span className="block text-white" data-id="element-3913" data-mp="3ip7n22wv8eww">
                {isEs ? 'Mayor Conexión' : 'Greater Connection'}
              </span>
              <span className="block" style={{
              color: RED
            }} data-id="element-3914" data-mp="1kyeisreojsrq">
                {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
              </span>
            </h2>
            <p className="font-nunito text-white/70 text-base sm:text-lg leading-relaxed mb-9" data-id="element-3915" data-mp="2yvr87q4vw2ew">
              {isEs ? 'Queremos construir esto con ustedes, no simplemente para ustedes. Si su organización sirve a las comunidades hondureñas o hondureño-americanas, nos encantaría conocerla.' : 'We want to build this with you, not simply for you. If your organization serves Honduran or Honduran-American communities, we would be honored to know you.'}
            </p>
            <a href={joinHref} className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
            color: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
            e.currentTarget.style.color = '#ffffff';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = NAVY;
          }} data-id="element-3916" data-mp="2kuevrooxmixr">
              <MailIcon className="w-4 h-4" data-id="element-3917" data-mp="217ypmjk2mc6d" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-white py-9 border-t" style={{
      borderColor: 'rgba(11,31,63,0.12)'
    }} data-id="element-3918" data-mp="2f6gb0ami7cl4">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-5" data-id="element-3919" data-mp="2a8et3t0tdjb8">
          <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-12 w-auto object-contain" data-id="element-3920" data-mp="1mg81f761a0i5" />
          <p className="font-nunito text-[10px] uppercase tracking-[0.22em]" style={{
          color: 'rgba(11,31,63,0.5)'
        }} data-id="element-3921" data-mp="1w9osi3esd1i4">
            {isEs ? 'Una iniciativa de la Asociación Hondureño-Americana' : 'An initiative of the Honduran-American Association'}
          </p>
        </div>
      </footer>
    </div>;
}